import { BAR_POINT, OFF_POINT } from '@kfiroo/backgammon-core'

import { AnimatedMove, BoardInfo, LaneInfo, PlayerType } from './GameView'

function cloneBoard(b: BoardInfo): BoardInfo {
  const lanes: Record<string, LaneInfo> = {}
  for (const [point, lane] of Object.entries(b.lanes)) {
    lanes[point] = { owner: lane.owner, checkers: lane.checkers }
  }
  return { lanes, bar: { ...b.bar }, off: { ...b.off } }
}

const otherOwner = (owner: PlayerType): PlayerType =>
  owner === PlayerType.OPPONENT ? PlayerType.PLAYER : PlayerType.OPPONENT

/**
 * Advance a BoardInfo by exactly one move (used to step the buffered
 * "displayed board" forward as each arc finishes). Pure — never mutates input.
 * Applying every projected move of a turn to the committed board reproduces
 * what InProgressGameApi.getBoard() returns.
 */
export function applyAnimatedMove(
  board: BoardInfo,
  move: AnimatedMove,
): BoardInfo {
  const next = cloneBoard(board)
  const mover = move.owner

  // Remove from source.
  if (move.from === BAR_POINT) {
    next.bar[mover] = Math.max(0, next.bar[mover] - 1)
  } else {
    const src = next.lanes[move.from]
    if (src && src.checkers > 1) {
      next.lanes[move.from] = { owner: src.owner, checkers: src.checkers - 1 }
    } else {
      delete next.lanes[move.from]
    }
  }

  // Hit: the defender's single blot at the destination goes to its bar.
  if (move.isHit) {
    const defender = otherOwner(mover)
    next.bar[defender] = next.bar[defender] + 1
    delete next.lanes[move.to]
  }

  // Add to destination.
  if (move.to === OFF_POINT) {
    next.off[mover] = next.off[mover] + 1
  } else {
    const dest = next.lanes[move.to]
    next.lanes[move.to] =
      dest && dest.owner === mover
        ? { owner: mover, checkers: dest.checkers + 1 }
        : { owner: mover, checkers: 1 }
  }

  return next
}
